import { isNil } from 'lodash';
import { lazy, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { addEvent } from '@/modules/api';
import { useEvent, useTheme } from '@/modules/common';
import { useBehaviour } from '@/modules/context';
import { EventType, PopupState, WidgetLayout, WidgetPopupType } from '@/modules/models';
import {
  useInitPopup,
  useInitPopupWithCloseBtn,
  useIsWidgetDisabled,
  useStuckPopup,
  useViewedProductsPopup,
} from '@/modules/widget/hooks';
import { useCrossSalePopup } from '@/modules/widget/hooks/useCrossSalePopup';
import {
  bridgeAtom,
  isOpenChatAtom,
  semiOpenSeenPagesAtom,
  sessionAtom,
  useSetValue,
  useValue,
  widgetPopupsStatesAtom,
} from '@/store';
import { AppEvents, publishShopifyAnalytics } from '@/utils';

const DefaultWidget = lazy(() => import('./layouts').then((module) => ({ default: module.DefaultLayout })));

export const Widget = () => {
  const { toggleChat } = useBehaviour();
  const {
    mediaMobile,
    widget: { widgetLayout },
  } = useTheme();

  const { initPopupExclusions, isEmbedded, widget, onClickPopupCloseBtn, pageType, translations, semiOpenConfig } =
    useValue(bridgeAtom);

  const [isCloseBtnVisible, setIsCloseBtnVisible] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  const [isHover, setIsHover] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<WidgetPopupType | null>(null);
  const isOpenChat = useValue(isOpenChatAtom);
  const session = useValue(sessionAtom);
  const semiOpenSeenPages = useValue(semiOpenSeenPagesAtom);

  const isTooltipOpenRef = useRef(false);
  const nextTooltipRef = useRef<WidgetPopupType | null>(null);
  const ignoreHoverRef = useRef(false);
  const hideTooltipAfterFadeOut = useRef(false);

  const setWidgetPopupsStates = useSetValue(widgetPopupsStatesAtom);

  const {
    crossSaleTooltip = false,
    stuckTooltip = false,
    productsTooltip = false,
    initTooltip = false,
    isPopupClosable = false,
  } = widget || {};

  const isWidgetDisabled = useIsWidgetDisabled();

  const isWidgetHidden = Boolean(isOpenChat || isWidgetDisabled || isEmbedded);
  const disabledPopup = isWidgetDisabled || isWidgetHidden;
  const isSemiOpenPagesMode = Boolean(semiOpenConfig?.pageTypes?.length);
  const isInitPopupDisabledForSemiOpen = Boolean(
    semiOpenConfig &&
    ((isSemiOpenPagesMode && semiOpenConfig.pageTypes?.includes(pageType)) ||
      (!isSemiOpenPagesMode && semiOpenSeenPages.length === 0))
  );
  const isInitPopupDisabledForPageType = Boolean(initPopupExclusions?.[pageType] || isInitPopupDisabledForSemiOpen);

  const toggleTooltip = useCallback((state?: boolean) => {
    setIsTooltipOpen((prevState) => (!isNil(state) ? state : !prevState));
  }, []);

  type InitKeys = keyof typeof translations.widget.initPopup;
  type HoverKeys = keyof typeof translations.widget.hoverPopup;

  const pageTypeInitMessage = window.LLL(translations?.widget?.initPopup?.[pageType as InitKeys]) || '';
  const defaultInitMessage = window.LLL(translations?.widget?.initPopup?.DEFAULT) || '';
  const initPopupMessage = pageTypeInitMessage.length ? pageTypeInitMessage : defaultInitMessage;
  const pageTypeHoverMessage = window.LLL(translations?.widget?.hoverPopup?.[pageType as HoverKeys]) || '';
  const defaultHoverMessage = window.LLL(translations?.widget?.hoverPopup?.DEFAULT) || '';
  const hoverPopupMessage = pageTypeHoverMessage || defaultHoverMessage || initPopupMessage;
  const stuckPopupMessage = window.LLL(translations.widget.stuckPopup) || '';
  const crossSalePopupMessage = window.LLL(translations.widget.crossSalePopup) || '';
  const viewedProductsPopupMessage = window.LLL(translations.widget.viewedProductsPopup) || '';

  const getPopupText = useCallback(
    (popupType: WidgetPopupType): string => {
      switch (popupType) {
        case WidgetPopupType.INIT:
        case WidgetPopupType.INIT_WITH_CLOSE_BTN:
          return initPopupMessage;

        case WidgetPopupType.STUCK_ON_PAGE:
          return stuckPopupMessage;

        case WidgetPopupType.CROSS_SALE:
          return crossSalePopupMessage;

        case WidgetPopupType.VIEWED_PRODUCTS:
          return viewedProductsPopupMessage;

        case WidgetPopupType.HOVER:
          return hoverPopupMessage;

        default:
          return defaultInitMessage;
      }
    },
    [
      initPopupMessage,
      stuckPopupMessage,
      crossSalePopupMessage,
      viewedProductsPopupMessage,
      hoverPopupMessage,
      defaultInitMessage,
    ]
  );

  const onToggleTooltipEvent = useCallback(
    (event: CustomEvent<{ isOpenTooltip: boolean; showCloseBtn: boolean }>) => {
      const { isOpenTooltip, showCloseBtn } = event.detail;
      const text = getPopupText(WidgetPopupType.HOVER);

      if (isOpenTooltip && !text.length) {
        return;
      }

      setActiveTooltip(WidgetPopupType.HOVER);
      setTooltipText(text);
      toggleTooltip(isOpenTooltip ?? null);
      if (showCloseBtn) {
        setIsCloseBtnVisible(true);
      }
    },
    [getPopupText, toggleTooltip]
  );

  const hideTooltip = useCallback(() => {
    toggleTooltip(false);
    setActiveTooltip(null);
  }, [toggleTooltip]);

  const isMobile = window.matchMedia(`(${mediaMobile})`).matches;

  const toggleHover = useCallback(
    (state: boolean) => {
      const shouldIgnoreHover = ignoreHoverRef.current && state;
      if (isMobile || shouldIgnoreHover) {
        return;
      }

      setIsHover(state);

      const isDefaultTooltip = activeTooltip === WidgetPopupType.HOVER;

      if (!state && !isDefaultTooltip) {
        return;
      }

      if (activeTooltip === null && state) {
        const text = getPopupText(WidgetPopupType.HOVER);
        if (!text.length) {
          return;
        }
        setActiveTooltip(WidgetPopupType.HOVER);
        setTooltipText(text);
      } else if (!state && isDefaultTooltip) {
        setActiveTooltip(null);
      }

      toggleTooltip(state);
    },
    [toggleTooltip, activeTooltip, isMobile, getPopupText]
  );

  const setPopupState = useCallback(
    (popupType: WidgetPopupType, popupState: PopupState) => {
      if (popupType !== WidgetPopupType.INIT) {
        setWidgetPopupsStates((prevState) => ({
          ...prevState,
          [popupType]: popupState,
        }));
      } else {
        setWidgetPopupsStates((prevState) => ({
          ...prevState,
          [WidgetPopupType.INIT]: {
            ...prevState[WidgetPopupType.INIT],
            [pageType]: popupState,
          },
        }));
      }
    },
    [setWidgetPopupsStates, pageType]
  );

  const closePopup = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      ignoreHoverRef.current = true;
      hideTooltipAfterFadeOut.current = true;

      onClickPopupCloseBtn?.();
      toggleTooltip(false);
      setActiveTooltip(null);
      setWidgetPopupsStates((prevState) => ({
        ...prevState,
        [WidgetPopupType.INIT_WITH_CLOSE_BTN]: PopupState.CLOSED,
        [WidgetPopupType.INIT]: {
          ...prevState[WidgetPopupType.INIT],
          [pageType]: PopupState.SEEN,
        },
      }));
    },
    [pageType, toggleTooltip, setWidgetPopupsStates, onClickPopupCloseBtn]
  );

  const showPopup = useCallback(
    (popupType: WidgetPopupType) => {
      setPopupState(popupType, PopupState.VISIBLE);
      if (isPopupClosable || popupType === WidgetPopupType.INIT_WITH_CLOSE_BTN) {
        setIsCloseBtnVisible(true);
      }
      setActiveTooltip(popupType);
      const popupText = getPopupText(popupType);
      setTooltipText(popupText);
      toggleTooltip(true);
    },
    [getPopupText, setPopupState, toggleTooltip, isPopupClosable]
  );

  const showNextTooltip = useCallback(() => {
    if (!nextTooltipRef.current) {
      return;
    }
    const nextTooltip = nextTooltipRef.current;
    nextTooltipRef.current = null;

    const timer = setTimeout(() => {
      showPopup(nextTooltip);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showPopup]);

  const onTooltipFadeOut = useCallback(() => {
    ignoreHoverRef.current = false;
    setIsCloseBtnVisible(false);
    if (nextTooltipRef.current) {
      showNextTooltip();
      return;
    }

    const text = getPopupText(WidgetPopupType.HOVER);
    setTooltipText(text);
  }, [showNextTooltip, getPopupText]);

  const startPopupWithDelay = useCallback(
    (popupType: WidgetPopupType, delay: number) => {
      const timer = setTimeout(() => {
        if (isTooltipOpenRef.current) {
          nextTooltipRef.current = popupType;
        } else {
          showPopup(popupType);
        }
      }, delay);

      return () => clearTimeout(timer);
    },
    [showPopup]
  );

  const onClick = useCallback(() => {
    toggleChat({ isOpenChat: true });

    if (tooltipText) {
      void addEvent({
        type: EventType.CLICKED_TOOLTIP,
        userSession: session,
        metadata: {
          pageType,
          tooltip: tooltipText,
        },
      });
      publishShopifyAnalytics('saywhatt_chat_clicked_tooltip', { page_type: pageType, tooltip: tooltipText });
    }
  }, [tooltipText, pageType, session, toggleChat]);

  useEffect(() => {
    isTooltipOpenRef.current = isTooltipOpen;
  }, [isTooltipOpen]);

  useInitPopupWithCloseBtn({
    isDisabled: !initTooltip || disabledPopup || isInitPopupDisabledForPageType || !initPopupMessage.length,
    isWidgetHidden,
    startPopupWithDelay,
  });

  useInitPopup({
    isDisabled: !initTooltip || disabledPopup || isInitPopupDisabledForPageType || !initPopupMessage.length,
    isWidgetHidden,
    isHover,
    startPopupWithDelay,
    hideTooltip,
    setPopupState,
  });

  useViewedProductsPopup({
    isDisabled: !productsTooltip || disabledPopup || !viewedProductsPopupMessage.length,
    activePopup: activeTooltip,
    isHover,
    isWidgetHidden,
    setPopupState,
    startPopupWithDelay,
    hidePopup: hideTooltip,
  });

  useStuckPopup({
    isDisabled: !stuckTooltip || disabledPopup || !stuckPopupMessage.length,
    isHover,
    hidePopup: hideTooltip,
    activePopup: activeTooltip,
    showPopup,
    setPopupState,
  });

  useCrossSalePopup({
    isDisabled: !crossSaleTooltip || disabledPopup || !crossSalePopupMessage.length,
    isHover,
    hidePopup: hideTooltip,
    startPopupWithDelay,
    setPopupState,
  });

  const [prevIsOpenChat, setPrevIsOpenChat] = useState(isOpenChat);
  if (isOpenChat !== prevIsOpenChat) {
    setPrevIsOpenChat(isOpenChat);
    setIsTooltipOpen(false);
  }

  useEvent({ name: AppEvents.TOGGLE_TOOLTIP, handler: onToggleTooltipEvent });

  if (isOpenChat || isWidgetDisabled || isEmbedded) {
    return null;
  }

  switch (widgetLayout) {
    case WidgetLayout.DEFAULT:
      return (
        <DefaultWidget
          closeBtn={isCloseBtnVisible}
          isTooltipOpen={isTooltipOpen}
          tooltipText={tooltipText}
          onTooltipHided={onTooltipFadeOut}
          toggleHover={toggleHover}
          closeTooltip={closePopup}
          onClick={onClick}
        />
      );

    default:
      return (
        <DefaultWidget
          closeBtn={isCloseBtnVisible}
          isTooltipOpen={isTooltipOpen}
          tooltipText={tooltipText}
          onTooltipHided={onTooltipFadeOut}
          toggleHover={toggleHover}
          closeTooltip={closePopup}
          onClick={onClick}
        />
      );
  }
};
